import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Paper from '@material-ui/core/Paper';
import { Title, useNotify } from 'react-admin';
import useFirebaseToken from '../../bitComponents/utils/hooks/useFirebaseToken';
import labels from '../../labels';
import { INVALID_CAR_NAMES_MANAGEMENT_URL } from '../../constants/constants';
import { clearCache, invalidateInvalidCarNamesCdn } from '../UICache/api';

const networkErrorMessage = (e) =>
  e &&
  (e.message === 'Failed to fetch' ||
    e.name === 'TypeError' ||
    String(e.message || '').includes('NetworkError'));

const MaintenanceList = () => {
  const notify = useNotify();
  const { token } = useFirebaseToken();
  const [loadingUi, setLoadingUi] = useState(false);
  const [loadingCdn, setLoadingCdn] = useState(false);
  const [loadingBoth, setLoadingBoth] = useState(false);
  const [uiStatus, setUiStatus] = useState(null);
  const [cdnStatus, setCdnStatus] = useState(null);
  const [bothStatus, setBothStatus] = useState(null);

  const busy = loadingUi || loadingCdn || loadingBoth;

  const cdnUrlConfigured = Boolean(
    String(INVALID_CAR_NAMES_MANAGEMENT_URL || '').trim()
  );

  const handleClearUiRedis = async () => {
    if (!token) {
      notify('יש להמתין רגע ולנסות שוב.', 'warning');
      return;
    }
    setLoadingUi(true);
    setUiStatus(null);
    setBothStatus(null);
    try {
      await clearCache(token);
      setUiStatus({
        type: 'success',
        message: 'ניקוי מטמון Redis הושלם בהצלחה.'
      });
      notify('ניקוי מטמון Redis הושלם בהצלחה.', 'success');
    } catch (e) {
      const msg = networkErrorMessage(e)
        ? 'לא ניתן להשלים את הפעולה כרגע. נסו שוב מאוחר יותר.'
        : 'הפעולה נכשלה.';
      setUiStatus({ type: 'error', message: msg });
      notify(msg, 'warning');
    } finally {
      setLoadingUi(false);
    }
  };

  const handleInvalidateCdn = async () => {
    if (!token) {
      notify('יש להמתין רגע ולנסות שוב.', 'warning');
      return;
    }
    if (!cdnUrlConfigured) {
      notify('הפעולה אינה זמינה כרגע.', 'warning');
      return;
    }
    setLoadingCdn(true);
    setCdnStatus(null);
    setBothStatus(null);
    try {
      await invalidateInvalidCarNamesCdn(token);
      setCdnStatus({
        type: 'success',
        message: 'ביטול מטמון ה-CDN הושלם בהצלחה.'
      });
      notify('ביטול מטמון ה-CDN הושלם בהצלחה.', 'success');
    } catch (e) {
      const msg = networkErrorMessage(e)
        ? 'לא ניתן להשלים את הפעולה כרגע. נסו שוב מאוחר יותר.'
        : 'הפעולה נכשלה.';
      setCdnStatus({ type: 'error', message: msg });
      notify(msg, 'warning');
    } finally {
      setLoadingCdn(false);
    }
  };

  const handleClearBoth = async () => {
    if (!token) {
      notify('יש להמתין רגע ולנסות שוב.', 'warning');
      return;
    }
    if (!cdnUrlConfigured) {
      notify('הפעולה אינה זמינה כרגע.', 'warning');
      return;
    }
    setLoadingBoth(true);
    setUiStatus(null);
    setCdnStatus(null);
    setBothStatus(null);
    try {
      const [redisResult, cdnResult] = await Promise.allSettled([
        clearCache(token),
        invalidateInvalidCarNamesCdn(token)
      ]);
      const redisOk = redisResult.status === 'fulfilled';
      const cdnOk = cdnResult.status === 'fulfilled';
      if (redisOk && cdnOk) {
        const msg = 'ניקוי שתי השכבות הושלם בהצלחה.';
        setBothStatus({ type: 'success', message: msg });
        notify(msg, 'success');
      } else if (redisOk || cdnOk) {
        const msg = 'רק חלק מהפעולות הצליחו. נסו שוב.';
        setBothStatus({ type: 'warning', message: msg });
        notify(msg, 'warning');
      } else {
        const msg = 'הפעולה נכשלה. נסו שוב מאוחר יותר.';
        setBothStatus({ type: 'error', message: msg });
        notify(msg, 'warning');
      }
    } finally {
      setLoadingBoth(false);
    }
  };

  return (
    <Box sx={{ paddingBottom: 100 }}>
      <Title title={labels.maintenance} />
      <Paper style={{ marginTop: 16 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              ניקוי מטמון אפליקציית הלקוח (אתר)
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              כאן מנקים את מטמון אפליקציית הלקוח כדי שמשתמשים יראו עדכון לאחר
              שינויים. יש שתי שכבות מטמון — Redis ו-CDN — ניתן לנקות כל שכבה בנפרד,
              או לנקות את שתיהן בלחיצה אחת בתחתית העמוד.
            </Typography>
            <Box marginTop={3}>
              <Typography variant="subtitle2" gutterBottom>
                שכבת Redis
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                מנקה את המטמון בצד השרת, כדי שהאפליקציה תיטען מחדש עם מידע
                עדכני.
              </Typography>
              <Box
                marginTop={2}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClearUiRedis}
                  disabled={busy || !token}
                >
                  {loadingUi ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <CircularProgress size={18} color="inherit" />
                      מנקה מטמון…
                    </Box>
                  ) : (
                    'ניקוי מטמון Redis'
                  )}
                </Button>
              </Box>
              {uiStatus && (
                <Box marginTop={2}>
                  <Alert severity={uiStatus.type}>{uiStatus.message}</Alert>
                </Box>
              )}
            </Box>
            <Box marginTop={3}>
              <Divider />
            </Box>
            <Box marginTop={3}>
              <Typography variant="subtitle2" gutterBottom>
                שכבת CDN
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                שכבה נפרדת מ-Redis. ביטול המטמון כאן עוזר לאפליקציה להציג תוכן
                עדכני למשתמשים.
              </Typography>
              <Box
                marginTop={2}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleInvalidateCdn}
                  disabled={busy || !token || !cdnUrlConfigured}
                >
                  {loadingCdn ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <CircularProgress size={18} color="inherit" />
                      מבצע…
                    </Box>
                  ) : (
                    'ביטול מטמון CDN'
                  )}
                </Button>
              </Box>
              {cdnStatus && (
                <Box marginTop={2}>
                  <Alert severity={cdnStatus.type}>{cdnStatus.message}</Alert>
                </Box>
              )}
            </Box>
          </CardContent>
          <CardActions
            style={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              padding: 16,
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 12
            }}
          >
            {bothStatus && (
              <Alert severity={bothStatus.type}>{bothStatus.message}</Alert>
            )}
            <Box display="flex" justifyContent="flex-start" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleClearBoth}
                disabled={busy || !token || !cdnUrlConfigured}
              >
                {loadingBoth ? (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={18} color="inherit" />
                    מנקה מטמון…
                  </Box>
                ) : (
                  'נקה הכל'
                )}
              </Button>
            </Box>
          </CardActions>
        </Card>
      </Paper>
    </Box>
  );
};

export default MaintenanceList;
